import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createResolution,
  deleteResolution,
  getResolution,
  listResolutions,
  updateResolution,
  type CreateResolutionInput,
  type ListResolutionsFilters,
  type UpdateResolutionInput,
} from '@/lib/resolutions'

export function useResolutions(filters: ListResolutionsFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['resolutions', filters],
    queryFn: () => listResolutions(filters),
    enabled,
  })
}

export function useResolution(id: string | undefined) {
  return useQuery({
    queryKey: ['resolutions', id],
    queryFn: () => getResolution(id!),
    enabled: !!id,
  })
}

export function useCreateResolution() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateResolutionInput) => createResolution(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resolutions'] }),
  })
}

export function useUpdateResolution() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateResolutionInput }) =>
      updateResolution(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['resolutions'] })
      qc.invalidateQueries({ queryKey: ['resolutions', id] })
    },
  })
}

export function useDeleteResolution() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteResolution(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resolutions'] }),
  })
}
